import { Box, Container, Typography, Grid, Button, Card, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import HandshakeIcon from '@mui/icons-material/Handshake';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// add props
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number,
  value: PropTypes.number,
};


const Home = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <DirectionsCarIcon sx={{ fontSize: 40 }} />,
      title: 'Easy Donation Process',
      description: 'Easy, hassle-free process, simply fill out the Donation Form with contact & vehicle details to have your vehicle picked up for FREE! Usually 24-48 hrs or less!',
      color: theme.palette.primary.main,
    },
    {
      icon: <MilitaryTechIcon sx={{ fontSize: 40 }} />,
      title: 'Veteran Support',
      description: 'Directly impact the lives of Young Veterans, Single Parent Veteran Families, and All Who Served Our Nation with essential resources, programs, and services.',
      color: theme.palette.secondary.main,
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
      title: 'Community Impact',
      description: 'By donating your vehicle, your generous contribution will assist in directly benefiting our Veteran Community in their time of need and post service lives',
      color: '#0066CC',
    },
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: '120px', sm: '140px', md: '160px' },
          pb: { xs: '60px', sm: '80px', md: '100px' },
          px: { xs: 2, sm: 4, md: 0 },
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: 'white',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 150%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 50%)',
          },
        }}
      >
        {/* Animated Bubbles */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ y: 100, opacity: 0 }}
            animate={{ 
              y: [-20, 20], 
              opacity: [0.3, 0.5, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              delay: i * 0.8,
              ease: "easeInOut"
            }}
            style={{
              position: 'absolute',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0))',
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
              pointerEvents: 'none',
            }}
          />
        ))}
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography 
                  variant="h1" 
                  sx={{ 
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                    fontWeight: 800,
                    mb: { xs: 2, md: 3 },
                    textShadow: '2px 4px 8px rgba(0,0,0,0.2)',
                    background: 'linear-gradient(to right, #ffffff, #f0f0f0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.02em',
                  }}
                >
                  Empowering Veterans Through Vehicle Donations

                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: { xs: 3, md: 4 },
                    opacity: 0.9,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    lineHeight: 1.6,
                    textShadow: '1px 2px 4px rgba(0,0,0,0.15)',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  Join us in assisting Young Veterans, Single Parent Veteran Families & All Who Served!
                </Typography>
                <Typography 
                  variant="h6"
                  sx={{ 
                    mb: { xs: 4, md: 5 },
                    opacity: 0.9,
                    fontSize: { xs: '1rem', sm: '1.15rem' },
                    lineHeight: 1.5,
                    textShadow: '1px 2px 4px rgba(0,0,0,0.15)',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-10px',
                      left: '0',
                      width: '60px',
                      height: '3px',
                      background: 'white',
                      borderRadius: '2px',
                    }
                  }}
                >
                  Donate Your Used Vehicle Today - All Years, Makes, Models & Conditions Accepted
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      borderRadius: '50px',
                    }}
                    component={Link}
                    to="/donate"
                  >
                    Donate Your Vehicle
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Veterans and Vehicles"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    transform: 'perspective(1000px) rotateY(-5deg)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'perspective(1000px) rotateY(0deg) scale(1.02)',
                    },
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Donation Process Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography 
          variant="h2" 
          align="center" 
          sx={{ 
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 700,
          }}
        >
          Your Vehicle Donation Process Is Free, Easy,
          At Your Convenience
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              title: "1. Notify Us",
              description:
                "Click the \"Donate Now\" Button, Call or Chat Live with a VFYV team member to schedule a free vehicle pickup at your earliest convenience. Call: 1(833) 838-8398",
              icon: <NotificationsActiveIcon sx={{ fontSize: 40 }} />
            },
            {
              title: "2. Quick Pick Up",
              description:
                "Your vehicle will be picked up quickly at the date and time that is convenient for you. (You don't need to be present)",
              icon: <img 
                src="/images/towing.png" 
                alt="Pickup Icon" 
                style={{ 
                  width: '60px', 
                  height: '60px',
                  filter: 'brightness(0) invert(1)' // This will make the image white
                }} 
              />
            },
            {
              title: "3. Receive Your Tax Deduction",
              description:
                "After your vehicle has been picked up you will receive an IRS tax donation receipt.",
              icon: <ReceiptIcon sx={{ fontSize: 40 }} />
            }
          ].map((step, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: { xs: 3, sm: 4 },
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mx: 'auto',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: 'white',
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  {index === 0 ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        Click the &quot;Donate Now&quot; Button, Call or Chat Live with a VFYV team member to schedule a free vehicle pickup at your earliest convenience.
                      </Typography>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                          Call:
                        </Typography>
                        <Box 
                          component="a" 
                          href="tel:+18338388398" 
                          sx={{ 
                            color: 'primary.main', 
                            textDecoration: 'none', 
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            '&:hover': { textDecoration: 'underline' } 
                          }}
                        >
                          1(833) 838-8398
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, flex: 1 }}>
                      {step.description}
                    </Typography>
                  )}
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Veterans Image Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          py: { xs: 8, md: 12 },
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            sx={{ 
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            Assist All Those Who Served Our Nation With Your Donation
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  mb: 4
                }}
              >
                <img
                  src="/images/5.jpeg"
                  alt="Young Veterans Honor Guard"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Card>
              <Box sx={{ 
                p: 4, 
                borderLeft: '4px solid',
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: '0 8px 8px 0',
                maxWidth: '800px',
                mx: 'auto',
                backdropFilter: 'blur(10px)'
              }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontStyle: 'italic',
                    mb: 2,
                    color: 'white',
                    lineHeight: 1.8,
                    textAlign: 'center'
                  }}
                >
                  &quot;Our debt to the heroic men and valiant women in the service of our country can never
                  be repaid. They have earned our undying gratitude&quot;
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                >
                  Harry S. Truman
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography
              variant="h3"
              textAlign="center"
              mb={6}
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #002868 0%, #BF0A30 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                textShadow: '0px 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              How we Help
            </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card 
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 4,
                    position: 'relative',
                    overflow: 'visible',
                    background: 'white',
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: '16px',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      '& .icon-box': {
                        transform: 'rotate(-10deg) scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box
                    className="icon-box"
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                      color: 'white',
                      transform: 'rotate(-10deg)',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, flex: 1 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Veterans Stories Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            textAlign="center"
            mb={6}
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #002868 0%, #BF0A30 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              textShadow: '0px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Making a Real Difference
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden', 
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src="/images/IMG_9817.jpeg"
                      alt="Young Veterans"
                      style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        p: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Assisting Young Veterans
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      Your vehicle donations help young veterans transition back to civilian life with the support they need. Every donation makes a meaningful impact.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Assisting Young Veterans Nationwide
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden', 
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src="/images/IMG_9726.jpeg"
                      alt="Single Parent Veterans"
                      style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        p: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Single Parent Veteran Families
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      We provide crucial support to single parent veteran families, ensuring they have the resources and assistance they deserve.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Supporting Veteran Families Nationwide
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Card sx={{ 
                  borderRadius: 2, 
                  overflow: 'hidden', 
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src="/images/3.jpeg"
                      alt="Veterans Support"
                      style={{ width: '100%', height: '250px', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                        p: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Community Impact
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                      Together, we&apos;re building a stronger community by helping veterans access the resources they need to thrive.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Making a Lasting Difference
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
     
      {/* Tax Deduction Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          py: { xs: 6, md: 8 },
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              fontWeight: 700,
            }}
          >
            Your generous vehicle donation is 100% tax deductible
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            You will be helping our veterans as well as receiving deductions on your taxes!
          </Typography>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 12,
          background: theme.palette.grey[50],
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}05 25%, transparent 25.1%)`,
          },
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 800 }}>
                Ready to Make a Difference?
              </Typography>
              <Typography variant="h5" sx={{ mb: 6, color: 'text.secondary' }}>
                Your vehicle donation can change a veteran&apos;s life today.
              </Typography>
              <Button
                component={Link}
                to="/donate"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  borderRadius: '50px',
                  textTransform: 'none',
                }}
              >
                Start Your Donation
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
